import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import DOMPurify from "dompurify";
import {
    FaDownload, FaExternalLinkAlt, FaFileAlt, FaFileExcel, FaFileImage,
    FaFilePdf, FaFileWord, FaFileCode, FaFilm, FaMusic, FaSyncAlt,
    FaSearchMinus, FaSearchPlus, FaCompress, FaTimes,
} from "react-icons/fa";
import { AppModal } from "../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldSelect } from "../../_CORE/APP/Templates/_UI/Form/_index";
import { ManualMarkdownEditor } from "../Views/Private/_Manuals/_UI/ManualMarkdownEditor";
import { API_BASE } from "../../_CORE/API/_Connection/_ApiBase";
import "./_Styles/UniversalFilePreview.css";

const TEXT_EXTENSIONS = new Set([
    "txt", "md", "markdown", "json", "csv", "tsv", "xml", "yaml", "yml", "ini", "conf", "config",
    "log", "sql", "sh", "bash", "zsh", "ps1", "bat", "cmd", "js", "jsx", "ts", "tsx", "css", "scss",
    "sass", "less", "html", "htm", "php", "py", "rb", "java", "c", "h", "cpp", "hpp", "cs", "go", "rs",
    "env", "gitignore", "dockerfile", "properties", "toml",
    "pub", "pem", "key", "ppk", "crt", "cer", "csr", "conf",
]);

const OFFICE_EXTENSIONS = new Set(["doc", "docx", "ppt", "pptx", "xls", "xlsx"]);
const EXCEL_EXTENSIONS = new Set(["xls", "xlsx", "xlsm", "ods"]);
const PRESENTATION_EXTENSIONS = new Set(["ppt", "pptx"]);

const MIME_BY_EXTENSION = {
    pdf: "application/pdf",
    png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp", svg: "image/svg+xml",
    mp4: "video/mp4", webm: "video/webm", mov: "video/quicktime",
    mp3: "audio/mpeg", wav: "audio/wav", ogg: "audio/ogg",
    txt: "text/plain", md: "text/markdown", markdown: "text/markdown", json: "application/json", csv: "text/csv",
    html: "text/html", htm: "text/html", xml: "application/xml",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};

const extensionOf = (file = {}) => {
    const name = String(file.originalName || file.name || file.title || "").trim();
    const index = name.lastIndexOf(".");
    return index >= 0 ? name.slice(index + 1).toLowerCase() : "";
};

const mimeOf = (file = {}, blob = null) => String(file.mimetype || file.mimeType || blob?.type || "").toLowerCase();
const fileNameOf = (file = {}) => file.originalName || file.name || file.title || "archivo";

const normalizeBlobMime = (file, value) => {
    if (!(value instanceof Blob)) return value;
    const current = String(value.type || "").toLowerCase();
    if (current && current !== "application/octet-stream") return value;
    const expected = MIME_BY_EXTENSION[extensionOf(file)];
    return expected ? new Blob([value], { type: expected }) : value;
};

const formatSize = (value) => {
    const bytes = Number(value || 0);
    if (!Number.isFinite(bytes) || bytes <= 0) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 ** 3) return `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
    return `${(bytes / (1024 ** 3)).toFixed(1)} GB`;
};


const resolveFileUrl = (value = "") => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    try {
        return new URL(raw, `${API_BASE.STATIC_URL}/`).toString();
    } catch {
        return raw;
    }
};

const isPublicHttpUrl = (value) => {
    try {
        const parsed = new URL(value);
        if (!["http:", "https:"].includes(parsed.protocol)) return false;
        return !["localhost", "127.0.0.1", "0.0.0.0"].includes(parsed.hostname);
    } catch {
        return false;
    }
};

const escapeHtml = (value = "") => String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const findZipEntry = async (arrayBuffer, requestedName) => {
    const bytes = new Uint8Array(arrayBuffer);
    const view = new DataView(arrayBuffer);
    let eocd = -1;
    const min = Math.max(0, bytes.length - 65557);

    for (let offset = bytes.length - 22; offset >= min; offset -= 1) {
        if (view.getUint32(offset, true) === 0x06054b50) {
            eocd = offset;
            break;
        }
    }
    if (eocd < 0) throw new Error("ZIP inválido o no compatible");

    const entries = view.getUint16(eocd + 10, true);
    let cursor = view.getUint32(eocd + 16, true);
    const decoder = new TextDecoder("utf-8");

    for (let index = 0; index < entries; index += 1) {
        if (view.getUint32(cursor, true) !== 0x02014b50) break;
        const method = view.getUint16(cursor + 10, true);
        const compressedSize = view.getUint32(cursor + 20, true);
        const fileNameLength = view.getUint16(cursor + 28, true);
        const extraLength = view.getUint16(cursor + 30, true);
        const commentLength = view.getUint16(cursor + 32, true);
        const localOffset = view.getUint32(cursor + 42, true);
        const name = decoder.decode(bytes.slice(cursor + 46, cursor + 46 + fileNameLength));

        if (name === requestedName) {
            if (view.getUint32(localOffset, true) !== 0x04034b50) throw new Error("Entrada ZIP inválida");
            const localNameLength = view.getUint16(localOffset + 26, true);
            const localExtraLength = view.getUint16(localOffset + 28, true);
            const start = localOffset + 30 + localNameLength + localExtraLength;
            const compressed = bytes.slice(start, start + compressedSize);

            if (method === 0) return compressed;
            if (method === 8 && typeof DecompressionStream !== "undefined") {
                const stream = new Blob([compressed]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
                return new Uint8Array(await new Response(stream).arrayBuffer());
            }
            throw new Error("Compresión DOCX no compatible con este navegador");
        }

        cursor += 46 + fileNameLength + extraLength + commentLength;
    }

    throw new Error(`No se encontró ${requestedName}`);
};

const listZipEntryNames = (arrayBuffer) => {
    const bytes = new Uint8Array(arrayBuffer);
    const view = new DataView(arrayBuffer);
    let eocd = -1;
    const min = Math.max(0, bytes.length - 65557);

    for (let offset = bytes.length - 22; offset >= min; offset -= 1) {
        if (view.getUint32(offset, true) === 0x06054b50) { eocd = offset; break; }
    }
    if (eocd < 0) return [];

    const entries = view.getUint16(eocd + 10, true);
    let cursor = view.getUint32(eocd + 16, true);
    const decoder = new TextDecoder("utf-8");
    const names = [];

    for (let index = 0; index < entries; index += 1) {
        if (view.getUint32(cursor, true) !== 0x02014b50) break;
        const fileNameLength = view.getUint16(cursor + 28, true);
        const extraLength = view.getUint16(cursor + 30, true);
        const commentLength = view.getUint16(cursor + 32, true);
        names.push(decoder.decode(bytes.slice(cursor + 46, cursor + 46 + fileNameLength)));
        cursor += 46 + fileNameLength + extraLength + commentLength;
    }
    return names;
};

const pptxToSlides = async (arrayBuffer) => {
    const names = listZipEntryNames(arrayBuffer)
        .filter((name) => /^ppt\/slides\/slide\d+\.xml$/i.test(name))
        .sort((left, right) => {
            const a = Number(left.match(/slide(\d+)\.xml/i)?.[1] || 0);
            const b = Number(right.match(/slide(\d+)\.xml/i)?.[1] || 0);
            return a - b;
        });

    const slides = [];
    for (let index = 0; index < names.length; index += 1) {
        const xmlBytes = await findZipEntry(arrayBuffer, names[index]);
        const xml = new TextDecoder("utf-8").decode(xmlBytes);
        const doc = new DOMParser().parseFromString(xml, "application/xml");
        if (doc.querySelector("parsererror")) continue;
        const paragraphs = Array.from(doc.getElementsByTagNameNS("*", "p"))
            .map((paragraph) => Array.from(paragraph.getElementsByTagNameNS("*", "t"))
                .map((node) => node.textContent || "").join(" ").trim())
            .filter(Boolean);
        slides.push({ number: index + 1, paragraphs });
    }
    return slides;
};

const renderDocxRun = (run) => {
    const texts = Array.from(run.getElementsByTagNameNS("*", "t")).map((node) => node.textContent || "").join("");
    const hasTab = run.getElementsByTagNameNS("*", "tab").length > 0;
    const hasBreak = run.getElementsByTagNameNS("*", "br").length > 0;
    let html = escapeHtml(texts);
    if (hasTab) html = `&emsp;${html}`;
    if (hasBreak) html += "<br>";
    const props = run.getElementsByTagNameNS("*", "rPr")[0];
    if (props?.getElementsByTagNameNS("*", "b").length) html = `<strong>${html}</strong>`;
    if (props?.getElementsByTagNameNS("*", "i").length) html = `<em>${html}</em>`;
    if (props?.getElementsByTagNameNS("*", "u").length) html = `<u>${html}</u>`;
    if (props?.getElementsByTagNameNS("*", "strike").length) html = `<s>${html}</s>`;
    return html;
};

const renderDocxParagraph = (paragraph) => {
    const runs = Array.from(paragraph.childNodes).filter((node) => node.localName === "r");
    const inner = runs.map(renderDocxRun).join("") || escapeHtml(paragraph.textContent || "");
    const styleNode = paragraph.getElementsByTagNameNS("*", "pStyle")[0];
    const style = styleNode?.getAttributeNS("http://schemas.openxmlformats.org/wordprocessingml/2006/main", "val")
        || styleNode?.getAttribute("w:val") || styleNode?.getAttribute("val") || "";
    const heading = String(style).match(/heading\s*([1-6])/i) || String(style).match(/titulo\s*([1-6])/i);
    if (heading) return `<h${heading[1]}>${inner || "&nbsp;"}</h${heading[1]}>`;
    const numbered = paragraph.getElementsByTagNameNS("*", "numPr").length > 0;
    return `<p>${numbered ? "<span class=\"LStudio__file_preview_docx_bullet\">•</span> " : ""}${inner || "&nbsp;"}</p>`;
};

const renderDocxTable = (table) => {
    const rows = Array.from(table.childNodes).filter((node) => node.localName === "tr");
    const body = rows.map((row) => {
        const cells = Array.from(row.childNodes).filter((node) => node.localName === "tc");
        return `<tr>${cells.map((cell) => `<td>${escapeHtml(cell.textContent || "")}</td>`).join("")}</tr>`;
    }).join("");
    return `<table><tbody>${body}</tbody></table>`;
};

const docxToHtml = async (arrayBuffer) => {
    const xmlBytes = await findZipEntry(arrayBuffer, "word/document.xml");
    const xml = new TextDecoder("utf-8").decode(xmlBytes);
    const doc = new DOMParser().parseFromString(xml, "application/xml");
    if (doc.querySelector("parsererror")) throw new Error("No se pudo interpretar el documento Word");
    const body = doc.getElementsByTagNameNS("*", "body")[0];
    if (!body) throw new Error("Documento Word vacío");
    const html = Array.from(body.childNodes).map((node) => {
        if (node.localName === "p") return renderDocxParagraph(node);
        if (node.localName === "tbl") return renderDocxTable(node);
        return "";
    }).join("");
    return DOMPurify.sanitize(html || "<p>Documento sin texto visible.</p>");
};

const inferKind = (file, blob) => {
    const ext = extensionOf(file);
    const mime = mimeOf(file, blob);
    const purpose = String(file?.purpose || "").toUpperCase();
    const name = fileNameOf(file).toLowerCase();
    if (ext === "pdf" || mime === "application/pdf") return "pdf";
    if (mime.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "avif"].includes(ext)) return "image";
    if (mime.startsWith("video/") || ["mp4", "webm", "mov", "m4v", "ogv"].includes(ext)) return "video";
    if (mime.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a", "aac", "flac"].includes(ext)) return "audio";
    if (ext === "docx" || mime.includes("wordprocessingml.document")) return "docx";
    if (ext === "pptx" || mime.includes("presentationml.presentation")) return "pptx";
    if (EXCEL_EXTENSIONS.has(ext) || mime.includes("spreadsheetml.sheet") || mime === "application/vnd.ms-excel") return "excel";
    if (PRESENTATION_EXTENSIONS.has(ext) || ext === "doc" || mime === "application/msword" || mime === "application/vnd.ms-powerpoint") return "office";
    if (ext === "md" || ext === "markdown" || mime.includes("markdown")) return "markdown";
    if (ext === "html" || ext === "htm" || mime.includes("text/html")) return "html";
    if (["PUBLIC_KEY", "PRIVATE_KEY", "SSH_PUBLIC_KEY", "SSH_PRIVATE_KEY", "KEY_FILE"].includes(purpose) || /^id_[a-z0-9_-]+(?:\.pub)?$/i.test(name)) return "text";
    if (TEXT_EXTENSIONS.has(ext) || mime.startsWith("text/") || mime.includes("json") || mime.includes("xml")) return "text";
    return "unknown";
};

const iconFor = (kind) => {
    if (kind === "pdf") return <FaFilePdf />;
    if (kind === "image") return <FaFileImage />;
    if (kind === "video") return <FaFilm />;
    if (kind === "audio") return <FaMusic />;
    if (kind === "docx" || kind === "pptx" || kind === "office") return <FaFileWord />;
    if (kind === "excel") return <FaFileExcel />;
    if (["text", "markdown", "html"].includes(kind)) return <FaFileCode />;
    return <FaFileAlt />;
};

export const UniversalFilePreview = ({ file, loadBlob = null, onDownload = null, className = "", showMeta = true }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [blob, setBlob] = useState(null);
    const [blobUrl, setBlobUrl] = useState(null);
    const [text, setText] = useState("");
    const [docxHtml, setDocxHtml] = useState("");
    const [pptxSlides, setPptxSlides] = useState([]);
    const [workbook, setWorkbook] = useState(null);
    const [sheetName, setSheetName] = useState("");
    const [imageZoom, setImageZoom] = useState(100);

    const ext = extensionOf(file);
    const kind = useMemo(() => inferKind(file, blob), [file, blob]);
    const resolvedUrl = useMemo(() => resolveFileUrl(file?.url), [file?.url]);
    const officeViewerUrl = useMemo(() => (
        OFFICE_EXTENSIONS.has(ext) && isPublicHttpUrl(resolvedUrl)
            ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(resolvedUrl)}`
            : ""
    ), [ext, resolvedUrl]);

    useEffect(() => {
        let active = true;
        let objectUrl = null;

        const run = async () => {
            if (!file) return;
            setLoading(true);
            setError("");
            setText("");
            setDocxHtml("");
            setPptxSlides([]);
            setWorkbook(null);
            setSheetName("");
            setImageZoom(100);

            try {
                let nextBlob = null;
                if (loadBlob) nextBlob = await loadBlob(file);
                else if (file.blob instanceof Blob) nextBlob = file.blob;
                else if (resolvedUrl) {
                    const response = await fetch(resolvedUrl, { credentials: "include" });
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    nextBlob = await response.blob();
                }
                if (!(nextBlob instanceof Blob)) throw new Error("El servidor no devolvió un archivo válido");
                nextBlob = normalizeBlobMime(file, nextBlob);
                if (!active) return;

                setBlob(nextBlob);
                objectUrl = URL.createObjectURL(nextBlob);
                setBlobUrl(objectUrl);

                const detected = inferKind(file, nextBlob);
                if (["text", "markdown", "html"].includes(detected)) {
                    let source = await nextBlob.text();
                    if (detected === "text" && ext === "json") {
                        try { source = JSON.stringify(JSON.parse(source), null, 2); } catch { /* texto original */ }
                    }
                    if (active) setText(source);
                } else if (detected === "docx") {
                    try {
                        const html = await docxToHtml(await nextBlob.arrayBuffer());
                        if (active) setDocxHtml(html);
                    } catch (docxError) {
                        if (!officeViewerUrl) throw docxError;
                    }
                } else if (detected === "pptx") {
                    try {
                        const slides = await pptxToSlides(await nextBlob.arrayBuffer());
                        if (active) setPptxSlides(slides);
                    } catch (pptxError) {
                        if (!officeViewerUrl) throw pptxError;
                    }
                } else if (detected === "excel") {
                    const data = await nextBlob.arrayBuffer();
                    const nextWorkbook = XLSX.read(data, { type: "array", cellDates: true });
                    if (active) {
                        setWorkbook(nextWorkbook);
                        setSheetName(nextWorkbook.SheetNames[0] || "");
                    }
                }
            } catch (nextError) {
                if (active) setError(nextError?.message || "No se pudo preparar la vista previa.");
            } finally {
                if (active) setLoading(false);
            }
        };

        run();
        return () => {
            active = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [file?.id, file?.url, file?.originalName, file?.name, loadBlob, officeViewerUrl, resolvedUrl, ext]);

    const sheetRows = useMemo(() => {
        if (!workbook || !sheetName || !workbook.Sheets[sheetName]) return [];
        return XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: "", raw: false });
    }, [workbook, sheetName]);

    const downloadCurrent = async () => {
        if (onDownload) return onDownload(file);
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileNameOf(file);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    if (!file) return null;

    if (loading) return <div className={`LStudio__file_preview_state ${className}`}><FaSyncAlt className="is-spinning" /><strong>Preparando vista previa...</strong><span>{fileNameOf(file)}</span></div>;

    if (error && !officeViewerUrl) return <div className={`LStudio__file_preview_state ${className}`}><FaFileAlt /><strong>No se pudo mostrar este archivo</strong><span>{error}</span><AppButton icon={<FaDownload />} onClick={downloadCurrent}>Descargar</AppButton></div>;

    return <div className={`LStudio__file_preview ${className}`} data-kind={kind}>
        {showMeta && <div className="LStudio__file_preview_meta">
            <span className="LStudio__file_preview_icon">{iconFor(kind)}</span>
            <div><strong>{fileNameOf(file)}</strong><small>{[mimeOf(file, blob), formatSize(file.size || blob?.size)].filter(Boolean).join(" · ") || "Vista previa"}</small></div>
            {officeViewerUrl && <AppButton size="sm" variant="outline" tone="neutral" icon={<FaExternalLinkAlt />} onClick={() => window.open(officeViewerUrl, "_blank", "noopener,noreferrer")}>Visor Office</AppButton>}
        </div>}

        <div className="LStudio__file_preview_body">
            {kind === "pdf" && blobUrl && <iframe src={`${blobUrl}#toolbar=1&navpanes=0&view=FitH`} title={fileNameOf(file)} className="LStudio__file_preview_frame" scrolling="auto" />}
            {kind === "image" && blobUrl && <>
                <div className="LStudio__file_preview_image_toolbar" role="toolbar" aria-label="Zoom de imagen">
                    <button type="button" onClick={() => setImageZoom((value) => Math.max(50, value - 25))} disabled={imageZoom <= 50} aria-label="Alejar" title="Alejar"><FaSearchMinus /></button>
                    <span>{imageZoom}%</span>
                    <button type="button" onClick={() => setImageZoom((value) => Math.min(300, value + 25))} disabled={imageZoom >= 300} aria-label="Ampliar" title="Ampliar"><FaSearchPlus /></button>
                    <button type="button" className="LStudio__file_preview_image_fit" onClick={() => setImageZoom(100)} aria-label="Ajustar imagen" title="Ajustar al visor"><FaCompress /><span>Ajustar</span></button>
                </div>
                <div className="LStudio__file_preview_media">
                    <div className="LStudio__file_preview_image_canvas" style={{ width: `${imageZoom}%` }}>
                        <img src={blobUrl} alt={fileNameOf(file)} />
                    </div>
                </div>
            </>}
            {kind === "video" && blobUrl && <div className="LStudio__file_preview_media"><video src={blobUrl} controls preload="metadata" /></div>}
            {kind === "audio" && blobUrl && <div className="LStudio__file_preview_audio"><audio src={blobUrl} controls preload="metadata" /></div>}
            {kind === "markdown" && <ManualMarkdownEditor value={text} readOnly minHeight={520} ariaLabel={`Vista previa de ${fileNameOf(file)}`} />}
            {kind === "html" && <iframe className="LStudio__file_preview_frame" sandbox="" title={fileNameOf(file)} srcDoc={DOMPurify.sanitize(text)} />}
            {kind === "text" && <pre className="LStudio__file_preview_text"><code>{text || "Archivo vacío."}</code></pre>}
            {kind === "docx" && docxHtml && <article className="LStudio__file_preview_docx" dangerouslySetInnerHTML={{ __html: docxHtml }} />}
            {kind === "docx" && !docxHtml && officeViewerUrl && <iframe src={officeViewerUrl} title={fileNameOf(file)} className="LStudio__file_preview_frame" />}
            {kind === "pptx" && pptxSlides.length > 0 && <div className="LStudio__file_preview_pptx">
                {pptxSlides.map((slide) => <section key={slide.number} className="LStudio__file_preview_slide">
                    <header>Diapositiva {slide.number}</header>
                    <div>{slide.paragraphs.length > 0 ? slide.paragraphs.map((paragraph, index) => <p key={`${slide.number}-${index}`}>{paragraph}</p>) : <p className="is-empty">Sin texto extraíble en esta diapositiva.</p>}</div>
                </section>)}
            </div>}
            {kind === "pptx" && pptxSlides.length === 0 && officeViewerUrl && <iframe src={officeViewerUrl} title={fileNameOf(file)} className="LStudio__file_preview_frame" />}
            {kind === "excel" && workbook && <div className="LStudio__file_preview_excel">
                <div className="LStudio__file_preview_excel_toolbar"><div className="LStudio__file_preview_excel_select"><FieldSelect id="preview_excel_sheet" label="Hoja" value={sheetName} onChange={(event) => setSheetName(event.target.value)} options={workbook.SheetNames.map((name) => ({ value: name, label: name }))} isSearchable={workbook.SheetNames.length >= 8} /></div><span>{sheetRows.length} filas</span></div>
                <div className="LStudio__file_preview_excel_table"><table><tbody>{sheetRows.slice(0, 500).map((row, rowIndex) => <tr key={rowIndex}>{row.slice(0, 60).map((cell, cellIndex) => <td key={`${rowIndex}-${cellIndex}`}>{String(cell ?? "")}</td>)}</tr>)}</tbody></table></div>
                {(sheetRows.length > 500 || sheetRows.some((row) => row.length > 60)) && <p className="LStudio__file_preview_note">La vista rápida muestra hasta 500 filas y 60 columnas. El archivo original conserva todo el contenido.</p>}
            </div>}
            {kind === "office" && officeViewerUrl && <iframe src={officeViewerUrl} title={fileNameOf(file)} className="LStudio__file_preview_frame" />}
            {kind === "office" && !officeViewerUrl && <div className="LStudio__file_preview_state"><FaFileWord /><strong>Este formato Office necesita un visor externo</strong><span>Los DOCX, XLS/XLSX y PPTX modernos se previsualizan dentro de NOTEBOOK. Para archivos DOC/PPT heredados, el visor web necesita una URL pública; el archivo sigue disponible para descarga.</span><AppButton icon={<FaDownload />} onClick={downloadCurrent}>Descargar archivo</AppButton></div>}
            {kind === "unknown" && <div className="LStudio__file_preview_state"><FaFileAlt /><strong>Vista previa no disponible en el navegador</strong><span>NOTEBOOK conserva el archivo y te permite descargarlo. Si el formato dispone de visor web, aparecerá como opción arriba.</span><AppButton icon={<FaDownload />} onClick={downloadCurrent}>Descargar archivo</AppButton></div>}
        </div>
    </div>;
};

export const UniversalFilePreviewModal = ({ file, isOpen, onClose, loadBlob = null, onDownload = null }) => {
    if (!file) return null;
    const kind = inferKind(file, null);
    const metadata = [mimeOf(file), formatSize(file?.size)].filter(Boolean).join(" · ") || "Vista previa";

    return <AppModal
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        className="LStudio__file_preview_modal"
        hasCloseButton={false}
    >
        <div className="LStudio__file_preview_reader">
            <header className="LStudio__file_preview_reader_header">
                <div className="LStudio__file_preview_reader_identity">
                    <span>{iconFor(kind)}</span>
                    <div>
                        <strong>{fileNameOf(file)}</strong>
                        <small>{metadata}</small>
                    </div>
                </div>

                <div className="LStudio__file_preview_reader_actions">
                    {onDownload && <AppButton variant="outline" tone="neutral" icon={<FaDownload />} onClick={() => onDownload(file)}>Descargar</AppButton>}
                    <button type="button" className="LStudio__file_preview_reader_close" onClick={onClose} aria-label="Cerrar vista previa" title="Cerrar"><FaTimes /></button>
                </div>
            </header>

            <div className="LStudio__file_preview_reader_content">
                <UniversalFilePreview file={file} loadBlob={loadBlob} onDownload={onDownload} showMeta={false} />
            </div>
        </div>
    </AppModal>;
};
